import { NgModule } from '@angular/core'
import { PreloadingStrategy, RouterModule, Routes } from '@angular/router'

import { Action, AppFeatures, Resource } from '@ti/contracts'
import { loggedInGuard, notLoggedInGuard } from '@ti/core/auth/guards'
import {
  allowOnEnv,
  checkAccess,
  fleetSmartRedirect,
  MaintenanceGuard,
  requirePricingPlan,
} from '@ti/core/common/guards'
import { provideAssistantConfig } from '@ti/shared/assistant/assistant.providers'

import { LayoutComponent } from './components/layout/layout.component'

const routes: Routes = [
  // Main Layout
  {
    path: '',
    component: LayoutComponent,
    canActivate: [loggedInGuard, MaintenanceGuard],
    children: [
      {
        path: 'bookmarks',
        loadChildren: () => import('@ti/fleet/bookmarks/bookmarks.routes'),
        canActivate: [
          checkAccess(Resource.Bookmarks, Action.View),
          requirePricingPlan([AppFeatures.Bookmarks]),
        ],
      },
      {
        path: 'dashboard',
        loadChildren: () => import('@ti/fleet/dashboard/dashboard.routes'),
        canActivate: [
          checkAccess(Resource.Dashboard, Action.View),
          requirePricingPlan([AppFeatures.Dashboard]),
        ],
      },
      {
        path: 'fleet',
        loadChildren: () => import('@ti/shared/fleet/fleet.routes'),
        canActivate: [checkAccess(Resource.Vehicle, Action.View)],
      },
      {
        path: 'locations',
        loadChildren: () => import('./routes/locations.routes'),
        canActivate: [checkAccess(Resource.Location, Action.View)],
      },
      {
        path: 'alerts',
        loadChildren: () => import('@ti/shared/alerts/alert.routes'),
        canActivate: [
          checkAccess({
            $or: [
              [Resource.Alert, Action.View],
              [Resource.AlertWorkflow, Action.View],
            ],
          }),
        ],
      },
      {
        path: 'video',
        loadChildren: () => import('@ti/shared/video/video.routes'),
        canActivate: [
          checkAccess(Resource.Media, Action.View),
          requirePricingPlan([AppFeatures.Video]),
        ],
      },
      {
        path: 'dispatch',
        loadChildren: () => import('@ti/fleet/dispatch/dispatch.routes'),
        canActivate: [
          checkAccess(Resource.Dispatch, Action.View),
          requirePricingPlan([AppFeatures.Dispatch]),
        ],
      },
      {
        path: 'maintenance',
        loadChildren: () => import('@ti/fleet/maintenance/maintenance.routes'),
        canActivate: [
          checkAccess(Resource.Maintenance, Action.View),
          requirePricingPlan([AppFeatures.Maintenance]),
        ],
      },
      {
        path: 'chat',
        loadChildren: () => import('@ti/shared/chat/chat.routes'),
        canActivate: [
          checkAccess(Resource.Driver, Action.View),
          requirePricingPlan([AppFeatures.DriverAccess]),
        ],
      },
      {
        path: 'ai-support',
        loadChildren: () => import('@ti/shared/assistant/assistant.routes'),
        providers: [
          provideAssistantConfig({
            routeBase: '/ai-support',
            allowDialogMode: true,
            hasSidebar: true,
          }),
        ],
      },
      {
        path: 'reports',
        loadChildren: () => import('@ti/fleet/reports/reports.routes'),
        canActivate: [checkAccess(Resource.Reports, Action.View)],
      },
      {
        path: '___telemetry',
        canMatch: [allowOnEnv(['local', 'dev'])],
        children: [
          {
            path: 'new',
            loadComponent: () =>
              import('./components/telemetry-read-mode/telemetry-read-mode-switch.component').then(
                (m) => m.TelemetryReadModeSwitchComponent,
              ),
            data: { telemetryReadMode: 'new' },
          },
          {
            path: 'legacy',
            loadComponent: () =>
              import('./components/telemetry-read-mode/telemetry-read-mode-switch.component').then(
                (m) => m.TelemetryReadModeSwitchComponent,
              ),
            data: { telemetryReadMode: 'legacy' },
          },
        ],
      },
      {
        path: 'settings',
        loadChildren: () => import('@ti/fleet/settings/fleet-settings.routes'),
      },
      {
        path: '',
        canActivate: [
          fleetSmartRedirect(
            {
              fleet: [Resource.Vehicle, Action.View],
              locations: [Resource.Location, Action.View],
              alerts: {
                $or: [
                  [Resource.Alert, Action.View],
                  [Resource.AlertWorkflow, Action.View],
                ],
              },
              video: [Resource.Media, Action.View],
              chat: [Resource.Driver, Action.View],
              reports: [Resource.Reports, Action.View],
              bookmarks: [Resource.Bookmarks, Action.View],
              dashboard: [Resource.Dashboard, Action.View],
              settings: {
                $or: [
                  [Resource.AuditLog, Action.View],
                  [Resource.Driver, Action.View],
                  [Resource.DrivingScore, Action.View],
                  [Resource.FleetSettings, Action.View],
                  [Resource.Group, Action.View],
                  [Resource.User, Action.View],
                  [Resource.Vehicle, Action.View],
                ],
              },
            },
            'fleet',
          ),
        ],
        loadComponent: () =>
          import('@ti/core/ui-kit/layout/components/not-found-page/not-found-page.component').then(
            (m) => m.NotFoundPageComponent,
          ),
      },
    ],
  },

  {
    path: 'link/:secret',
    loadChildren: () => import('@ti/fleet/shared-link/shared-link.routes'),
  },
  {
    path: 'media-link/:token',
    loadChildren: () => import('@ti/fleet/shared-link/shared-media.routes'),
  },

  // Sign-in, password and access-code pages are one-off steps the user doesn't come back to, so they
  // aren't kept for reuse
  {
    path: 'sign-in',
    loadComponent: () =>
      import('./components/sign-in/sign-in.component').then((m) => m.FleetSignInComponent),
    canActivate: [notLoggedInGuard, MaintenanceGuard],
    data: { title: 'sign-in.heading', reuseRoute: false },
  },
  {
    path: 'change-password',
    loadComponent: () =>
      import('@ti/shared/auth-ui/components/new-password/new-password.component').then(
        (m) => m.NewPasswordComponent,
      ),
    canActivate: [MaintenanceGuard],
    data: { title: 'auth.change-password.title', reuseRoute: false },
  },
  {
    path: 'create-password/:secret',
    loadComponent: () =>
      import('@ti/shared/auth-ui/components/new-password/new-password.component').then(
        (m) => m.NewPasswordComponent,
      ),
    canActivate: [MaintenanceGuard],
    data: { title: 'auth.create-password.title', reuseRoute: false },
  },
  {
    path: 'reset-password/:secret',
    loadComponent: () =>
      import('@ti/shared/auth-ui/components/new-password/new-password.component').then(
        (m) => m.NewPasswordComponent,
      ),
    canActivate: [MaintenanceGuard],
    data: { title: 'auth.reset-password.title', reuseRoute: false },
  },
  {
    path: '___design-system',
    canActivate: [loggedInGuard],
    canMatch: [allowOnEnv(['local', 'dev'])],
    loadComponent: () =>
      import('@ti/core/design-system/design-system.component').then((m) => m.DesignSystemComponent),
  },

  {
    path: 'access-code',
    loadComponent: () =>
      import('@ti/core/ui-kit/layout/components/access-code-page/access-code-page.component').then(
        (m) => m.AccessCodePageComponent,
      ),
    data: { reuseRoute: false },
  },

  {
    path: 'access-denied',
    loadComponent: () =>
      import('@ti/core/ui-kit/layout/components/access-denied-page/access-denied-page.component').then(
        (m) => m.AccessDeniedPageComponent,
      ),
  },
  {
    path: 'maintenance',
    loadComponent: () =>
      import('@ti/core/ui-kit/layout/components/maintenance-page/maintenance-page.component').then(
        (m) => m.MaintenancePageComponent,
      ),
  },
  {
    path: '**',
    loadComponent: () =>
      import('@ti/core/ui-kit/layout/components/not-found-page/not-found-page.component').then(
        (m) => m.NotFoundPageComponent,
      ),
  },
]

// TODO: Rework to standalone component routes
@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadingStrategy })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
